import rudiPortraitUrl from './assets/virchow.webp';
import { defaultPosterText } from './text';
import type { PosterSubmissionFields } from '@rudi/shared/types/clinical-trials';

export const posterT = defaultPosterText;
export const POSTER_FONT = 'Inter, Arial, sans-serif';
export const JOURNEY_FONT = '"Amatic SC", sans-serif';
export const JOURNEY_QR_BOUNDS = { x: 579, y: 762, width: 430, height: 430 };
export const CLASSIC_QR_BOUNDS = { x: 228, y: 1481, width: 416, height: 417 };

export const POSTER_W = 1587;
export const POSTER_H = 2245;
export const PORTRAIT_FRAME = { x: 24, y: 1915, width: 230, height: 300 };
export const POSTER_STYLES = ['classic', 'journey', 'doctor'] as const;
export type PosterStyle = (typeof POSTER_STYLES)[number] | 'official' | 'patient' | 'community';
export const DEFAULT_POSTER_IMAGE = rudiPortraitUrl;
export const CLASSIC_LAYERS = [
  'circle_gray_top_left.png',
  'circle_gray_top_right.png',
  'circle_blue_bottom_right.png',
  'circle_black_bottom_right.png',
  'doc_bottom_right.png',
  'Rudi_templates.png',
].map(file => `/clinical-trials/posters/templates/${file}`);

export const POSTER_THEMES = {
  classic: { background: '#ffffff', ink: '#050505', accent: '#08bad4', soft: '#edf9fa' },
  journey: { background: '#ffebdf', ink: '#151515', accent: '#ff6b0b', soft: '#fff5ef' },
  official: { background: '#ffffff', ink: '#172c45', accent: '#285b85', soft: '#eaf0f5' },
  doctor: { background: '#f5f4ec', ink: '#203e36', accent: '#347562', soft: '#e0e9de' },
  patient: { background: '#fdf5ec', ink: '#482b35', accent: '#a54252', soft: '#f0d9d4' },
  community: { background: '#202954', ink: '#ffffff', accent: '#f2d985', soft: '#343f6e' },
} as const;

export function posterTemplateId(style: PosterStyle): string {
  return style === 'classic' ? 'poster_ki_studienidentifikation' : `poster_${style}`;
}

export function initialPosterFields(
  style: PosterStyle,
  viewerLabel?: string | null
): PosterSubmissionFields {
  const fields: PosterSubmissionFields =
    style === 'journey'
      ? {
          header: posterT('clinicalTrials.posterStyles.journey.header'),
          subheader: posterT('clinicalTrials.posterStyles.journey.subheader'),
          banner: posterT('clinicalTrials.posterStyles.journey.banner'),
          bullets: [posterT('clinicalTrials.posterStyles.journey.bulletStudies')],
          checklist: [posterT('clinicalTrials.posterStyles.journey.cta')],
          bottomText: posterT('clinicalTrials.posterStyles.journey.bottom'),
        }
      : style === 'classic'
        ? {
            header: posterT('clinicalTrials.ui.discoverSuitableTherapyInnovationsNow'),
            subheader: posterT('clinicalTrials.posterDefaults.subheader'),
            banner: posterT('clinicalTrials.posterDefaults.banner'),
            bullets: [
              posterT('clinicalTrials.posterDefaults.bulletStudies'),
              posterT('clinicalTrials.posterDefaults.bulletContacts'),
            ],
            checklist: (['free', 'scientific', 'privacy'] as const).map(item =>
              posterT(`clinicalTrials.posterDefaults.${item}`)
            ),
            bottomText: posterT('clinicalTrials.posterDefaults.bottom'),
          }
        : {
            header: posterT(`clinicalTrials.posterStyles.${style}.header`),
            subheader: posterT(`clinicalTrials.posterStyles.${style}.subheader`),
            banner: posterT(`clinicalTrials.posterStyles.${style}.banner`),
            bullets: [
              posterT(`clinicalTrials.posterStyles.${style}.bulletStudies`),
              posterT(`clinicalTrials.posterStyles.${style}.bulletContacts`),
            ],
            checklist: (['checkOne', 'checkTwo', 'checkThree'] as const).map(item =>
              posterT(`clinicalTrials.posterStyles.${style}.${item}`)
            ),
            bottomText: posterT(`clinicalTrials.posterStyles.${style}.bottom`),
          };
  return {
    ...fields,
    bottomText:
      viewerLabel && !viewerLabel.includes('@')
        ? `${fields.bottomText}\n${viewerLabel}`
        : posterT('clinicalTrials.posterEditor.studyTeam'),
    bottomImageDataUrl: null,
    exportedPdfDataUrl: null,
  };
}

export interface PosterTextBlock {
  key: string;
  field: 'header' | 'subheader' | 'banner' | 'bullets' | 'checklist' | 'bottomText';
  index?: number;
  text: string;
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  size: number;
  weight: number;
  color: string;
  align: 'left' | 'center';
  serif?: boolean;
  lineHeight?: number;
  letterSpacing?: number;
  fontFamily?: string;
}

export function posterBlockFont(block: PosterTextBlock): string {
  return block.fontFamily ?? (block.serif ? 'Georgia, serif' : POSTER_FONT);
}

export async function loadPosterFonts(): Promise<void> {
  if (!document.fonts) return;
  await Promise.all([
    ...[400, 500, 600, 700, 800, 900].map(weight => document.fonts.load(`${weight} 48px Inter`)),
    document.fonts.load('400 48px "Amatic SC"'),
  ]);
}

export function posterTextBlocks(
  fields: PosterSubmissionFields,
  style: PosterStyle
): PosterTextBlock[] {
  const theme = POSTER_THEMES[style];
  const classic = style === 'classic';
  const centered = classic || style === 'patient';
  const block = (
    field: PosterTextBlock['field'],
    text: string,
    x: number,
    y: number,
    width: number,
    height: number,
    size: number,
    label: string,
    options: Partial<PosterTextBlock> = {}
  ): PosterTextBlock => ({
    key: field,
    field,
    text,
    x,
    y,
    width,
    height,
    size,
    label,
    weight: 400,
    color: theme.ink,
    align: centered ? 'center' : 'left',
    ...options,
  });
  if (style === 'journey') {
    const lettering = {
      align: 'center' as const,
      color: theme.accent,
      fontFamily: JOURNEY_FONT,
      lineHeight: 1.15,
    };
    return [
      block(
        'subheader',
        fields.subheader,
        160,
        160,
        1267,
        190,
        140,
        posterT('clinicalTrials.ui.topQuestion'),
        lettering
      ),
      block(
        'header',
        fields.header,
        100,
        385,
        1387,
        230,
        185,
        posterT('clinicalTrials.ui.mainHeading'),
        lettering
      ),
      block(
        'banner',
        fields.banner ?? '',
        105,
        1290,
        1377,
        130,
        96,
        posterT('clinicalTrials.posterStyles.journey.topicLabel'),
        lettering
      ),
      ...fields.bullets.map((text, index) =>
        block(
          'bullets',
          text,
          160,
          1425 + (index * 130) / Math.max(fields.bullets.length, 1),
          1267,
          Math.min(85, 130 / Math.max(fields.bullets.length, 1) - 8),
          64,
          posterT('clinicalTrials.ui.bulletValue1', { value1: index + 1 }),
          { ...lettering, key: `bullets-${index}`, index }
        )
      ),
      ...(fields.checklist ?? []).map((text, index, items) =>
        block(
          'checklist',
          text,
          160,
          1610 + (index * 170) / Math.max(items.length, 1),
          1267,
          170 / Math.max(items.length, 1) - 25,
          112,
          posterT('clinicalTrials.posterStyles.journey.ctaLabel'),
          { ...lettering, key: `checklist-${index}`, index }
        )
      ),
      block(
        'bottomText',
        fields.bottomText,
        fields.portraitHidden ? 100 : 300,
        1990,
        fields.portraitHidden ? 1360 : 1160,
        185,
        43,
        posterT('clinicalTrials.ui.bottomText'),
        { align: 'left' }
      ),
    ];
  }
  return [
    block(
      'subheader',
      fields.subheader,
      classic ? 160 : 130,
      classic ? 125 : 165,
      classic ? 1267 : 1327,
      160,
      classic ? 55 : 48,
      posterT('clinicalTrials.ui.topQuestion'),
      { weight: classic ? 700 : 600 }
    ),
    block(
      'header',
      fields.header,
      classic ? 115 : 130,
      classic ? 350 : 370,
      classic ? 1357 : 1327,
      classic ? 255 : 300,
      classic ? 88 : 108,
      posterT('clinicalTrials.ui.mainHeading'),
      {
        weight: classic ? 900 : style === 'doctor' ? 500 : 800,
        lineHeight: classic ? 1.45 : 1.25,
        color: theme.accent,
        serif: style === 'doctor' || style === 'patient',
      }
    ),
    block(
      'banner',
      fields.banner ?? '',
      classic ? 60 : 130,
      classic ? 682 : 730,
      classic ? 1175 : 1327,
      classic ? 112 : 140,
      classic ? 51 : 46,
      posterT('clinicalTrials.ui.blackBar'),
      {
        weight: 700,
        color: classic ? '#ffffff' : theme.ink,
        align: 'left',
        lineHeight: classic ? 1.06 : 1.25,
        letterSpacing: classic ? 2 : 0,
      }
    ),
    ...fields.bullets.map((text, index) =>
      block(
        'bullets',
        text,
        classic ? 140 : 190,
        (classic ? 850 : 925) +
          index * ((classic ? 280 : 340) / Math.max(fields.bullets.length, 1)),
        classic ? 1240 : 1210,
        Math.max(20, (classic ? 280 : 340) / Math.max(fields.bullets.length, 1) - 26),
        48,
        posterT('clinicalTrials.ui.bulletValue1', { value1: index + 1 }),
        { key: `bullets-${index}`, index, align: 'left' }
      )
    ),
    ...(fields.checklist ?? []).map((text, index, items) =>
      block(
        'checklist',
        text,
        classic ? 242 : 210,
        (classic ? 1170 : 1290) + index * ((classic ? 270 : 330) / Math.max(items.length, 1)),
        classic ? 730 : 1150,
        Math.max(20, (classic ? 270 : 330) / Math.max(items.length, 1) - 26),
        classic ? 48 : 47,
        posterT('clinicalTrials.ui.checkValue1', { value1: index + 1 }),
        { key: `checklist-${index}`, index, align: 'left' }
      )
    ),
    block(
      'bottomText',
      fields.bottomText,
      fields.portraitHidden ? 100 : classic ? 285 : 400,
      classic ? 2055 : 1900,
      fields.portraitHidden ? 1365 : classic ? 1180 : 1050,
      classic ? 132 : 210,
      classic ? 43 : 37,
      posterT('clinicalTrials.ui.bottomText'),
      { color: classic ? '#050505' : theme.ink, align: classic ? 'left' : 'center' }
    ),
  ];
}

let measureContext: CanvasRenderingContext2D | null | undefined;
export function fitPosterText(block: PosterTextBlock): { lines: string[]; size: number } {
  if (measureContext === undefined) {
    measureContext =
      typeof CanvasRenderingContext2D === 'undefined'
        ? null
        : document.createElement('canvas').getContext('2d');
  }
  const wrap = (size: number): string[] => {
    if (measureContext) measureContext.font = `${block.weight} ${size}px ${posterBlockFont(block)}`;
    const width = (value: string) =>
      (measureContext?.measureText(value).width ?? value.length * size * 0.55) +
      Math.max(0, value.length - 1) * (block.letterSpacing ?? 0);
    const lines: string[] = [];
    for (const paragraph of block.text.replace(/\r/g, '').split('\n')) {
      let line = '';
      for (const word of paragraph.split(/\s+/).filter(Boolean)) {
        if (line && width(`${line} ${word}`) > block.width) {
          lines.push(line);
          line = '';
        }
        for (const char of `${line ? ' ' : ''}${word}`) {
          if (line && width(line + char) > block.width) {
            lines.push(line);
            line = '';
          }
          line += char;
        }
      }
      lines.push(line);
    }
    return lines;
  };
  let size = block.size;
  let lines = wrap(size);
  while (lines.length * size * (block.lineHeight ?? 1.25) > block.height && size > 1) {
    size -= 1;
    lines = wrap(size);
  }
  return { lines, size };
}
